import { useEffect } from 'react';
import { makeStyles, Theme } from '@material-ui/core/styles';
import { Paper, GridColumnProps } from '@roc/ui';
import { observer } from 'mobx-react';
import { ProtectiveAdvancesGrid } from '../protectiveAdvancesGrid';
import { CounterPartyExpensesTab, PayableReceivablesTypes, FRONTED, RECEIVABLE_BILLS_TABS, getPayableReceivablesStatusOptions } from '../../types/types';
import { useReceivablesDashboardStore } from '../../hooks/useReceivablesDasboardStore';
import { dateFilterProps, filterProps, noOpComparator, selectFilterProps, selectSearchFilterProps } from '@roc/ui/utils';
import { ReceivablesToolbar } from '../receivables/receivablesToolbar';
import { Box, ListItemIcon, ListItemText, MenuItem } from '@material-ui/core';
import { CloudDownload, NoteAdd } from '@material-ui/icons';
import { useDocumentStore } from '@roc/feature-documents';
import { insertIf } from '@roc/feature-utils';
import { ReceivableBillDetailsModal } from './receivableBillDetailsModal';




const useStyles = makeStyles((theme: Theme) => ({
  container: {
    //Same as nportal dashboard
    maxWidth: '1400px',
  },
  paper: {
    boxShadow: 'none !important',
    border: '1px solid #cdcdcd',
    padding: '0px',
    borderRadius: '0 !important',
  },
}));


export const ReceivablesBillsDashboard = observer(() => {
  const classes = useStyles();
  const { receivablesBillsStore, receivablesDashboardStore: { investorOptions } } = useReceivablesDashboardStore();
  const { tab } = receivablesBillsStore;

  useEffect(() => {
    receivablesBillsStore.fetchEntityNames();
  }, []);

  const RECEIVABLES_BILLS_COLUMNS: GridColumnProps[] = [
    {
      field: 'billId',
      headerName: 'Bill Id',
      width: 90,
      cellRenderer: 'linkColumnCellRenderer',
      comparator: noOpComparator,
      ...filterProps(),
    },
    {
      field: 'entity',
      headerName: 'Investor',
      minWidth: 125,
      comparator: noOpComparator,
      ...selectSearchFilterProps(null, () => investorOptions),
    },
    {
      field: 'status',
      headerName: 'Receivable Status',
      width: 125,
      comparator: noOpComparator,
      ...selectFilterProps(getPayableReceivablesStatusOptions(tab)),
      editable: true,
      cellEditor: 'statusReceivablesBillsEditor',
      cellRenderer: 'statusCellRenderer',
    },
    {
      field: 'amount',
      headerName: 'Total Bill Amount',
      minWidth: 125,
      cellRenderer: 'currencyCellRenderer',
      comparator: noOpComparator,
      ...filterProps(),
    },
    {
      field: 'investorPaidAmount',
      headerName: 'Investor Paid Amount',
      minWidth: 125,
      cellRenderer: 'currencyCellRenderer',
      comparator: noOpComparator,
      ...filterProps(),
    },
    {
      field: 'investorPendingAmount',
      headerName: 'Investor Pending Amount',
      minWidth: 125,
      cellRenderer: 'currencyCellRenderer',
      comparator: noOpComparator,
      ...filterProps(),
    },
    {
      field: 'investorPaidDate',
      headerName: 'Investor Paid Date',
      minWidth: 125,
      cellRenderer: 'dateCellRenderer',
      comparator: noOpComparator,
      ...dateFilterProps(),
    },
    {
      field: 'createdDate',
      headerName: 'Creation Date',
      minWidth: 125,
      cellRenderer: 'dateCellRenderer',
      comparator: noOpComparator,
      ...dateFilterProps(),
    },
    {
      field: 'comments',
      headerName: 'Comments',
      minWidth: 125,
      comparator: noOpComparator,
      ...filterProps(),
      editable: true,
      cellEditor: 'commentEditor',
      cellEditorParams: {
        type: tab,
      },
    },
    {
      field: 'leadId',
      headerName: 'Action',
      minWidth: 80,
      cellRenderer: 'actionCellRenderer',
      comparator: noOpComparator,
      width: 80,
      pinned: 'right',
      lockPinned: true,
    },
  ];

  const actions = (record, handleMenuClose) => {

    return (
      [
        <MenuItem
          onClick={() => {
            receivablesBillsStore.downloadBillDocuments([record.recordId]);
            handleMenuClose();
          }}
        >
          <ListItemIcon>
            <CloudDownload fontSize="small" />
          </ListItemIcon>
          <ListItemText>Download Invoices Files</ListItemText>
        </MenuItem>,
        <MenuItem
          onClick={() => {
            receivablesBillsStore.exportBillInvoicesData(record.recordId);
            handleMenuClose();
          }}
        >
          <ListItemIcon>
            <CloudDownload fontSize="small" />
          </ListItemIcon>
          <ListItemText>Download Invoices Report</ListItemText>
        </MenuItem>,
        ...insertIf(record.internalInvoice && PayableReceivablesTypes.BILLED === record.status, [
          <MenuItem
            onClick={() => {
              receivablesBillsStore.performBillAction(record.recordId, FRONTED);
              handleMenuClose();
            }}
          >
            <ListItemIcon>
              <NoteAdd fontSize="small" />
            </ListItemIcon>
            <ListItemText>Create Investor Invoices</ListItemText>
          </MenuItem>
        ]),
      ]);
  }


  return (
    <>
      <Box display="flex" flexDirection="column" height="100%">
        <Box flex={1}>
          <Paper className={classes.paper}>
            <ProtectiveAdvancesGrid
              counterPartyExpenseStore={receivablesBillsStore}
              columns={RECEIVABLES_BILLS_COLUMNS}
              selectModeEnabled={true}
              toolbar={
                <ReceivablesToolbar
                  tabs={RECEIVABLE_BILLS_TABS}
                  receivablesDashboardStore={receivablesBillsStore}
                />}
              actions={actions}
            />
          </Paper>
        </Box>
      </Box>
      <ReceivableBillDetailsModal />
    </>
  );
});
